import { computed, type Ref } from 'vue';

import type {
  CalendarCellDisplay,
  CalendarRow,
  CapacityAllocationMap,
} from '@/types/capacityCalendar';
import { aggregateAllocationsInRange, aggregateMatchedInRange, aggregatePendingInRange, buildDayAllocationPlacements } from '@/utils/capacityCalendar';
import {
  generateBuckets,
  type CalendarBucket,
  type CalendarGranularity,
} from '@/utils/calendarBuckets';
import type { PendingCell } from '@/utils/importPreview';

export type DisplayGranularity = CalendarGranularity;

function cellKey(rowId: string, bucketKey: string): string {
  return `${rowId}|${bucketKey}`;
}

export function useCapacityCalendar(
  granularity: Ref<DisplayGranularity>,
  rows: Ref<CalendarRow[]>,
  allocations: Ref<CapacityAllocationMap>,
  // Import-preview overlay, keyed by `${rowId}:${weekStart}` — optional so callers outside
  // the calendar page (none today) aren't forced to thread an empty map through.
  pendingCells?: Ref<Map<string, PendingCell>>,
  // Import-preview "already matches the calendar" markers, keyed the same way — disjoint from
  // pendingCells by construction (see buildImportDiff): a week is either new/changed or matched,
  // never both.
  matchedCells?: Ref<Map<string, true>>
) {
  const buckets = computed(() => generateBuckets(granularity.value));
  const weekBuckets = computed(() => generateBuckets('week'));
  const bucketByKey = computed(
    () => new Map(buckets.value.map((bucket) => [bucket.key, bucket]))
  );
  const currentBucketKey = computed(
    () => buckets.value.find((bucket) => bucket.isCurrent)?.key ?? null
  );

  const cellDisplays = computed(() => {
    const displays = new Map<string, CalendarCellDisplay>();

    if (granularity.value === 'week') {
      rows.value.forEach((row) => {
        buckets.value.forEach((bucket) => {
          displays.set(cellKey(row.id, bucket.key), {
            allocation: allocations.value[row.id]?.[bucket.key] ?? null,
            columnSpan: 1,
            hidden: false,
          });
        });
      });
      return displays;
    }

    // Month cells roll the weeks starting inside the month into one read-only summary.
    if (granularity.value === 'month') {
      rows.value.forEach((row) => {
        buckets.value.forEach((bucket) => {
          displays.set(cellKey(row.id, bucket.key), {
            allocation: aggregateAllocationsInRange(allocations.value[row.id] ?? {}, bucket.startDate, bucket.endDate),
            columnSpan: 1,
            hidden: false,
          });
        });
      });
      return displays;
    }

    const placements = buildDayAllocationPlacements(
      buckets.value,
      weekBuckets.value.map((bucket) => bucket.key)
    );
    const bucketIndex = new Map(buckets.value.map((bucket, index) => [bucket.key, index]));

    rows.value.forEach((row) => {
      buckets.value.forEach((bucket) => {
        displays.set(cellKey(row.id, bucket.key), {
          allocation: null,
          columnSpan: 1,
          hidden: false,
        });
      });

      placements.forEach((placement, startBucketKey) => {
        const startIndex = bucketIndex.get(startBucketKey);
        if (startIndex === undefined) {
          return;
        }
        displays.set(cellKey(row.id, startBucketKey), {
          allocation: allocations.value[row.id]?.[placement.weekKey] ?? null,
          columnSpan: placement.visibleDaySpan,
          hidden: false,
        });

        Array.from({ length: placement.visibleDaySpan - 1 }, (_, index) => index + 1).forEach(
          (offset) => {
            const coveredBucket = buckets.value[startIndex + offset];
            if (coveredBucket) {
              displays.set(cellKey(row.id, coveredBucket.key), {
                allocation: null,
                columnSpan: 1,
                hidden: true,
              });
            }
          }
        );
      });
    });

    return displays;
  });

  function getCellDisplay(rowId: string, bucketKey: string): CalendarCellDisplay {
    return cellDisplays.value.get(cellKey(rowId, bucketKey)) ?? {
      allocation: null,
      columnSpan: 1,
      hidden: false,
    };
  }

  // Resolves a pending-import overlay for a rendered cell the same way cellDisplays resolves
  // real allocations, so overlays land on the correct bucket in every granularity:
  // - week: the bucket key IS the pending cell's weekStart, a direct lookup.
  // - day: only the placement's start-of-week day carries the week's data (see
  //   buildDayAllocationPlacements) — the same day gets that week's overlay.
  // - month: several weeks can start inside one month bucket; aggregatePendingInRange rolls
  //   them into one synthetic overlay (mirrors aggregateAllocationsInRange for real data).
  const pendingDisplays = computed(() => {
    const displays = new Map<string, PendingCell | null>();
    const pendingMap = pendingCells?.value;
    if (!pendingMap || pendingMap.size === 0) {
      return displays;
    }

    if (granularity.value === 'week') {
      rows.value.forEach((row) => {
        buckets.value.forEach((bucket) => {
          displays.set(cellKey(row.id, bucket.key), pendingMap.get(`${row.id}:${bucket.key}`) ?? null);
        });
      });
      return displays;
    }

    if (granularity.value === 'month') {
      rows.value.forEach((row) => {
        buckets.value.forEach((bucket) => {
          const weekKeysInRange = weekBuckets.value
            .filter((weekBucket) => weekBucket.startDate <= bucket.endDate && weekBucket.endDate >= bucket.startDate)
            .map((weekBucket) => weekBucket.key);
          displays.set(cellKey(row.id, bucket.key), aggregatePendingInRange(pendingMap, row.id, weekKeysInRange));
        });
      });
      return displays;
    }

    const placements = buildDayAllocationPlacements(
      buckets.value,
      weekBuckets.value.map((bucket) => bucket.key)
    );
    rows.value.forEach((row) => {
      placements.forEach((placement, startBucketKey) => {
        displays.set(cellKey(row.id, startBucketKey), pendingMap.get(`${row.id}:${placement.weekKey}`) ?? null);
      });
    });
    return displays;
  });

  function getPendingDisplay(rowId: string, bucketKey: string): PendingCell | null {
    return pendingDisplays.value.get(cellKey(rowId, bucketKey)) ?? null;
  }

  // Resolves a matched-import marker for a rendered cell the same way pendingDisplays resolves
  // pending overlays (see its comment) — week/day is a direct/placement lookup, month rolls up
  // to "any week in range matched".
  const matchedDisplays = computed(() => {
    const displays = new Map<string, boolean>();
    const matchedMap = matchedCells?.value;
    if (!matchedMap || matchedMap.size === 0) {
      return displays;
    }

    if (granularity.value === 'week') {
      rows.value.forEach((row) => {
        buckets.value.forEach((bucket) => {
          displays.set(cellKey(row.id, bucket.key), matchedMap.has(`${row.id}:${bucket.key}`));
        });
      });
      return displays;
    }

    if (granularity.value === 'month') {
      rows.value.forEach((row) => {
        buckets.value.forEach((bucket) => {
          const weekKeysInRange = weekBuckets.value
            .filter((weekBucket) => weekBucket.startDate <= bucket.endDate && weekBucket.endDate >= bucket.startDate)
            .map((weekBucket) => weekBucket.key);
          displays.set(cellKey(row.id, bucket.key), aggregateMatchedInRange(matchedMap, row.id, weekKeysInRange));
        });
      });
      return displays;
    }

    const placements = buildDayAllocationPlacements(
      buckets.value,
      weekBuckets.value.map((bucket) => bucket.key)
    );
    rows.value.forEach((row) => {
      placements.forEach((placement, startBucketKey) => {
        displays.set(cellKey(row.id, startBucketKey), matchedMap.has(`${row.id}:${placement.weekKey}`));
      });
    });
    return displays;
  });

  function getMatchedDisplay(rowId: string, bucketKey: string): boolean {
    return matchedDisplays.value.get(cellKey(rowId, bucketKey)) ?? false;
  }

  function getBucketForColumn(columnId: string): CalendarBucket | null {
    return bucketByKey.value.get(columnId) ?? null;
  }

  return {
    buckets,
    currentBucketKey,
    getBucketForColumn,
    getCellDisplay,
    getPendingDisplay,
    getMatchedDisplay,
  };
}
